import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const useMileageData = ({ mileageUrl, getAuthHeaders, accountId, user }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [trips, setTrips] = useState([]);
  const [odometerYearStart, setOdometerYearStart] = useState(null);
  const [odometerYearEnd, setOdometerYearEnd] = useState(null);
  const [defaultMileageRate, setDefaultMileageRate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchMileage = useCallback(async (year) => {
    const targetYear = year || selectedYear;
    setIsLoading(true);
    setError('');
    setTrips([]);
    setOdometerYearStart(null);
    setOdometerYearEnd(null);
    setDefaultMileageRate(null);
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(
        `${mileageUrl}/${targetYear}?sortBy=tripDate&sortOrder=desc`,
        config
      );
      if (response.data.success) {
        const data = response.data.data;
        setTrips(data.trips || []);
        setOdometerYearStart(data.odometerYearStart);
        setOdometerYearEnd(data.odometerYearEnd);
        setDefaultMileageRate(data.defaultMileageRate ?? null);
      } else {
        setError(response.data.error || 'Failed to fetch mileage data');
      }
    } catch (err) {
      console.error('Error fetching mileage:', err);
      setError('Failed to load mileage data');
    } finally {
      setIsLoading(false);
    }
  }, [mileageUrl, getAuthHeaders, selectedYear]);

  useEffect(() => {
    if (user && accountId) {
      fetchMileage(selectedYear);
    }
  }, [user, selectedYear, accountId, fetchMileage]);

  const createTrip = async (formData) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${mileageUrl}/${selectedYear}/trips`,
        formData,
        config
      );
      if (response.data.success) {
        setTrips(prev => [response.data.data, ...prev]);
        return true;
      } else {
        const errorMsg = response.data.validationErrors
          ? response.data.validationErrors.join('. ')
          : response.data.error || 'Failed to create trip';
        setError(errorMsg);
        return false;
      }
    } catch (err) {
      console.error('Error creating trip:', err);
      const errorMsg = err.response?.data?.validationErrors
        ? err.response.data.validationErrors.join('. ')
        : err.response?.data?.error || 'Failed to create trip';
      setError(errorMsg);
      return false;
    }
  };

  const updateTrip = async (tripId, formData) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.put(
        `${mileageUrl}/${selectedYear}/trips/${tripId}`,
        formData,
        config
      );
      if (response.data.success) {
        setTrips(prev =>
          prev.map(trip => trip.tripId === tripId ? response.data.data : trip)
        );
        return true;
      } else {
        const errorMsg = response.data.validationErrors
          ? response.data.validationErrors.join('. ')
          : response.data.error || 'Failed to update trip';
        setError(errorMsg);
        return false;
      }
    } catch (err) {
      console.error('Error updating trip:', err);
      setError(err.response?.data?.error || 'Failed to update trip');
      return false;
    }
  };

  const deleteTrip = async (tripId) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.delete(
        `${mileageUrl}/${selectedYear}/trips/${tripId}`,
        config
      );
      if (response.data.success) {
        setTrips(prev => prev.filter(trip => trip.tripId !== tripId));
        return true;
      } else {
        setError(response.data.error || 'Failed to delete trip');
        return false;
      }
    } catch (err) {
      console.error('Error deleting trip:', err);
      setError(err.response?.data?.error || 'Failed to delete trip');
      return false;
    }
  };

  const updateMileageRate = async (value) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.put(
        `${mileageUrl}/${selectedYear}/rate`,
        { defaultMileageRate: value },
        config
      );
      if (response.data.success) {
        setDefaultMileageRate(response.data.data.defaultMileageRate ?? null);
        return true;
      } else {
        const errorMsg = response.data.validationErrors
          ? response.data.validationErrors.join('. ')
          : response.data.error || 'Failed to update mileage rate';
        setError(errorMsg);
        return false;
      }
    } catch (err) {
      console.error('Error updating mileage rate:', err);
      const errorMsg = err.response?.data?.validationErrors
        ? err.response.data.validationErrors.join('. ')
        : err.response?.data?.error || 'Failed to update mileage rate';
      setError(errorMsg);
      return false;
    }
  };

  const updateOdometer = async (odometerData) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.put(
        `${mileageUrl}/${selectedYear}/odometer`,
        odometerData,
        config
      );
      if (response.data.success) {
        const data = response.data.data;
        setOdometerYearStart(data.odometerYearStart);
        setOdometerYearEnd(data.odometerYearEnd);
        return true;
      } else {
        setError(response.data.error || 'Failed to update odometer readings');
        return false;
      }
    } catch (err) {
      console.error('Error updating odometer:', err);
      setError(err.response?.data?.error || 'Failed to update odometer readings');
      return false;
    }
  };

  return {
    selectedYear,
    setSelectedYear,
    trips,
    odometerYearStart,
    odometerYearEnd,
    defaultMileageRate,
    isLoading,
    error,
    setError,
    fetchMileage,
    createTrip,
    updateTrip,
    deleteTrip,
    updateOdometer,
    updateMileageRate,
  };
};

export default useMileageData;
